import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { PREVIEW_ITEM } from "../../store/actions/configurationActions";
import { getItemStates, getShowPrice, getSummaryItems } from "../../store/sagas/selectors";
import GaReporter from "../../utilities/gaReporter";
import selected from "../../icons/check.png";

import { Item } from "../../models/item";
import { Delimiter } from "../../models/delimiter";
import { TRIGGER_PREFERRED_ANGLE } from "../../store/actions/viewActions";
import { usePriceFormatter } from "../../utilities/priceFormatter";

interface ItemContainerProps {
    isSelected: boolean;
}

const ItemsContainer = styled.div`
    display: flex;
    flex-flow: row wrap;
    position: relative;
`;

const ItemContainer = styled.div<ItemContainerProps>`
    width: 100%;
    flex-flow: column nowrap;
    align-items: flex-start;
    justify-content: center;
    display: flex;
    border-radius: 4px;
    padding: 16px 18px;
    margin-bottom: 20px;
    ${({ theme }) => theme.bp.landscape`
        padding: 8px 10px;
        margin-bottom: 12px;
    `};
    ${({ theme }) => theme.bp.tb`
        padding: 16px 18px;
        margin-bottom: 20px;
    `};
    ${({ theme }) => theme.bp.landscape`
        &:last-child {
            margin-bottom: 0;
        }
    `};
    backdrop-filter: blur(5px);
    cursor: pointer;
    overflow: hidden;

    transition: background 0.1s ease-in-out;

    color: #333333;
    background: rgba(255, 255, 255, 0.6);
    &:hover {
        background: rgba(229, 229, 229, 0.8);
    }
    &:active {
        background: rgba(212, 212, 212, 0.9);
    }

    ${props =>
        props.isSelected
            ? `
    ::after{
        content: "";
        width: 15px;
        height: 15px;
        position: absolute;
        background: url(${selected});
        right: 5px;
        background-size: contain;
    }`
            : ``}
`;

const ItemText = styled.p`
    font-size: 16px;
    ${({ theme }) => theme.bp.landscape`
        font-size: 13px;
    `};
    flex: 1 0 auto;
    overflow: hidden;
    max-width: 100%;
    text-overflow: ellipsis;
    width: 100%;
    text-align: left;
    white-space: pre-wrap;
    margin: 0;
    font-weight: 500;
`;

const ItemPrice = styled.div`
    font-size: 14px;
    ${({ theme }) => theme.bp.landscape`
        font-size: 13px;
    `}
    font-weight: normal;
    margin-top: 3px;
`;

const DelimiterContainer = styled.div`
    margin: 2px 6px 12px 18px;
    font-size: 16px;
    ${({ theme }) => theme.bp.landscape`
        font-size: 14px;
        font-weight: 500;
        margin-left: 6px;
        margin-top: 10px;
    `};
    ${({ theme }) => theme.bp.tbLandscape`
        margin-top: 17px;
    `};
    color: #333333;
    text-shadow: #ffffff4f 0px 0px 5px, #ffffffba 0px 0px 15px, white 0px 0px 40px, white 0px 0px 80px;
    &:first-child {
        margin-top: 0;
    }
`;

export const ItemList = ({ items }: { items: (Item | Delimiter)[] | null }) => {
    const dispatch = useDispatch();
    const showPrice = useSelector(getShowPrice);
    const summmaryItems = useSelector(getSummaryItems);
    const itemStates = useSelector(getItemStates);
    const priceFormatter = usePriceFormatter(true);

    const onItemClick = (item: Item) => {
        const price = itemStates.getPrice(item.id);
        GaReporter.trackProductClick(item, price, "Item menu");

        dispatch({ type: PREVIEW_ITEM, item });
        if (item.preferredAngles) {
            dispatch({ type: TRIGGER_PREFERRED_ANGLE, preferredAngles: item.preferredAngles });
        }
    };

    return (
        <ItemsContainer>
            {items?.map((item: Item | Delimiter) => {
                if (item instanceof Delimiter) {
                    return <DelimiterContainer key={item.id}>{item.displayText}</DelimiterContainer>;
                }

                return (
                    <ItemContainer
                        key={item.id}
                        isSelected={summmaryItems.some(s => s.item.id === item.id)}
                        onClick={() => onItemClick(item)}
                    >
                        <ItemText>{item.displayText || item.retailerId || item.id}</ItemText>
                        {showPrice && (
                            <ItemPrice>{priceFormatter.formatDisplayPrice(itemStates.getPrice(item.id))}</ItemPrice>
                        )}
                    </ItemContainer>
                );
            })}
        </ItemsContainer>
    );
};
